import React from 'react';
import { Link } from 'react-router-dom';

import Toggle from 'components/partials/toggle/toggle';

import type { ClientGoalDto } from 'api/generated/schemas';

type GoalRow = ClientGoalDto;

type LinkedGoals = Array<string | number> | Array<GoalRow>;

interface RowArg<T = GoalRow> {
  row: { original: T };
}

interface RelatedGoalsColumn {
  header: () => React.JSX.Element;
  accessorKey?: string;
  id?: string;
  headerClassName: string;
  className: string;
  enableSorting: boolean;
  isHeaderCentered?: boolean;
  cell?: (info: RowArg) => React.JSX.Element;
}

const commonStyles = 'pl-4 border-r-2 border-white font-semibold h-8';

export const getRelatedGoalsTableSettings = (
  linkedGoals: LinkedGoals,
  handleToggleChange: (goal: GoalRow, newValue: boolean) => void,
  isToggling: boolean,
  clientId: string | number | undefined
): RelatedGoalsColumn[] => [
  {
    header: () => <span>GOAL CATEGORY</span>,
    accessorKey: 'category',
    headerClassName: `${commonStyles} rounded-tl-md rounded-bl-md`,
    className: 'pl-4 py-3 tablet:w-[30%] font-semibold',
    enableSorting: false,
  },
  {
    header: () => <span>GOAL DESCRIPTION</span>,
    accessorKey: 'description',
    headerClassName: commonStyles,
    className: 'pl-4 py-3 tablet:w-[50%]',
    enableSorting: false,
    cell: ({ row }: RowArg) => {
      const goal = row.original;
      const goalText = goal.description || goal.summary || goal.category || '';
      const type = (goal.type ?? '').replace(' ', '-').toLowerCase();
      const category = (goal.category ?? '').replace(' ', '-').toLowerCase();

      const goalLink = `/admin/clients/${clientId}/goals/${goal.enteringClassYear}/${type}/${category}/${goal.id}`;

      return (
        <Link to={goalLink} className="text-blue-700 underline">
          {goalText}
        </Link>
      );
    },
  },
  {
    header: () => <span>LINK</span>,
    id: 'link',
    headerClassName: `${commonStyles} rounded-tr-md rounded-br-md`,
    className: 'pl-4 py-3 tablet:w-[20%]',
    isHeaderCentered: true,
    enableSorting: false,
    cell: ({ row }: RowArg) => {
      const goal = row.original;

      const isArrayOfIds =
        Array.isArray(linkedGoals) &&
        linkedGoals.length > 0 &&
        typeof linkedGoals[0] !== 'object';

      const isLinked = isArrayOfIds
        ? (linkedGoals as Array<string | number>).includes(goal.id ?? -1)
        : (linkedGoals as GoalRow[]).some((g) => g.id === goal.id);

      return (
        <div className="flex justify-center">
          <Toggle
            isToggleOn={isLinked}
            onToggleChange={(newValue: boolean) =>
              handleToggleChange(goal, newValue)
            }
            disabled={isToggling}
            data-testid="related-goals-table-toggle"
          />
        </div>
      );
    },
  },
];
